import { useEffect } from "react";
import { LocalizedString } from "@commercetools/platform-sdk";
import Script from "next/script";
import { ProductDetailContainer } from "@Components/productDetailPage";
import { DY_PAGE_CONTEXT } from "@Constants/common";
import CmsPage, { getCMSPageModel } from "@Libs/contentful/components/CmsPage";
import productDetailPageQuery from "@Libs/contentful/queries/ProductDetailPageQuery";
import { setDYContext } from "@Libs/dynamic-yield/dynamicYield";
import { getCtAllProducts, getCtDataBySlug } from "@Utils/HomePageHelpers";
import { nextAppEnv } from "@Utils/environment";
import { makeStaticProps } from "@wff/libs/i18n/getStatic";
import i18nextConfig from "@wff/next-i18next.config";
import { useAppSelector } from "@wff/store/hooks";
import { localeSelector } from "@wff/store/localeSlice";

interface IProductPageProps {
  pageModel: any;
  ctData: any;
}

const ProductPage = ({ pageModel, ctData }: IProductPageProps) => {
  const { pageContent = {} } = pageModel;
  const productData = { pageContent, ctData };
  const currentProduct = ctData?.masterData?.current;
  const sku = currentProduct?.masterVariant?.sku ?? "";
  const { languageCode } = useAppSelector(localeSelector);

  //Set 'PRODUCT' DY-PageContext
  useEffect(
    () => setDYContext(DY_PAGE_CONTEXT.PRODUCT, languageCode, [sku]),
    [sku, languageCode]
  );

  return (
    <>
      <Script
        id="product-review-yotpo"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `
          (function e(){var e=document.createElement("script");e.type="text/javascript",e.async=true,e.src="//staticw2.yotpo.com/${nextAppEnv.yotpo.appKey}/widget.js";var t=document.getElementsByTagName("script")[0];t.parentNode.insertBefore(e,t)})();
        `,
        }}
      />
      <CmsPage
        pageModel={pageModel}
        pageTitle={pageContent?.productData?.pageTitle}
        productSKU={sku}
        pageDescription={pageContent.productData?.pageDescription}
      >
        <ProductDetailContainer productData={productData} />
      </CmsPage>
    </>
  );
};

export async function getStaticPaths() {
  if (process.env.SKIP_BUILD_STATIC_GENERATION) {
    return {
      paths: [],
      fallback: "blocking",
    };
  }
  // Call an external API endpoint to get products
  const res = await getCtAllProducts();
  const products = res?.body?.results ? res?.body?.results : [];

  // Get the paths we want to prerender based on product
  // In production environments, prerender all pages
  // (slower builds, but faster initial page load)}

  const paths: {
    params: { slug: string | LocalizedString; locale: string };
  }[] = [];
  products.forEach((page: any) => {
    i18nextConfig?.i18n?.locales?.map((locale: string) => {
      const lng = locale;

      const slug = page?.masterData?.current?.slug[lng]
        ? page?.masterData?.current?.slug[lng]
        : "";

      if (slug) {
        paths.push({
          params: {
            slug: slug,
            locale: locale,
          },
        });
      }
    });
  });

  return { paths, fallback: false };
}

const overrideStaticProps = async (props: any) => {
  const { locale, slug } = props;
  const lang = locale;
  const ctData = await getCtDataBySlug(slug, lang);
  let productId = "";
  let productIndex = -1;
  const productsData = ctData?.body?.results ?? null;

  if (productsData) {
    productIndex = productsData.findIndex(
      (product) => product?.masterData?.current?.slug[lang] === slug
    );
    productId =
      productsData[productIndex]?.masterData?.current?.masterVariant?.sku ?? "";
  }

  // Get the page model content from Contentful
  const pageModel = await getCMSPageModel(productDetailPageQuery, {
    productId,
    locale,
    slug,
  });

  // add the returned data to the new model
  const newPageModel = {
    ...pageModel,
  };

  const productData = productsData ? productsData[productIndex] : null;
  return {
    props: {
      pageModel: newPageModel,
      ctData: productData,
    },
    revalidate: 10, // TODO:- Set revalidation timeout based on the Architecture discussion
  };
};

const getStaticProps = makeStaticProps(["common"], overrideStaticProps);
export { getStaticProps };

export default ProductPage;
